
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from '@/contexts/LanguageContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  redirectAdmins?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  adminOnly = false,
  redirectAdmins = false
}) => {
  const { user, isLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        toast({
          title: t('protectedRoute.accessRestricted'),
          description: t('protectedRoute.accessRestrictedDesc'),
          variant: "destructive",
        });
        navigate("/auth");
      } else if (adminOnly && !isAdmin) {
        toast({
          title: t('protectedRoute.accessDenied'),
          description: t('protectedRoute.accessDeniedDesc'),
          variant: "destructive",
        });
        navigate("/");
      } else if (redirectAdmins && isAdmin) {
        // Redirecionar administradores para o dashboard
        navigate("/dashboard");
      }
    }
  }, [user, isLoading, isAdmin, adminOnly, redirectAdmins, navigate, toast]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-brand-darker">
        <div className="h-16 w-16 animate-spin rounded-full border-b-2 border-t-2 border-brand-red"></div>
      </div>
    );
  }

  // Verifica se é uma rota de admin e se o usuário tem permissão
  if (adminOnly && !isAdmin) {
    return null;
  }

  // Verifica se devemos redirecionar admins
  if (redirectAdmins && isAdmin) {
    return null;
  }

  return <>{user && children}</>;
};

export default ProtectedRoute;
